import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, Signal, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  RouterModule,
} from "@angular/router";
import { filter, map } from "rxjs/operators";
import { BreadcrumbService } from "./breadcrumb.service";

export interface BreadcrumbData {
  label: string;
  url?: string;
}

@Component({
  selector: "app-breadcrumb",
  standalone: true,
  imports: [CommonModule, RouterModule],
  styleUrls: ["./breadcrumb.component.scss"],
  template: `
    <ul class="custom-bread-crumb">
      <li *ngFor="let item of breadcrumb(); let i = index">
        <a
          *ngIf="item.url"
          [routerLink]="item.url"
          class="bread-prev-link"
        >
          {{ item.label }}
        </a>

        <span *ngIf="!item.url" class="bread-curr-link">
          {{ item.label }}
        </span>

        <span class="line" *ngIf="i < breadcrumb().length - 1">
          {{ symbol }}
        </span>
      </li>
    </ul>
  `,
})
export class BreadcrumbComponent implements OnInit {
  @Input() symbol = " • ";

  private breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private breadcrumbSignal = toSignal(
    this.breadcrumbService.newBreadcrumb$,
    { initialValue: [] }
  );

  private labelDataSignal = toSignal(
    this.breadcrumbService.breadcrumbLabels$,
    { initialValue: {} }
  );

  /**
   * Recupera tutti i parametri delle route annidate
   */
  private getAllRouteParams(): Record<string, string> {
    let currentRoute: ActivatedRoute | null = this.route;
    const allParams: Record<string, string> = {};

    while (currentRoute) {
      Object.assign(allParams, currentRoute.snapshot.params);
      currentRoute = currentRoute.firstChild;
    }

    return allParams;
  }

  /**
   * Breadcrumb finale mostrato a UI
   */
  breadcrumb: Signal<BreadcrumbData[]> = computed(() => {
    const breadcrumbs = this.breadcrumbSignal();
    const labelData = this.labelDataSignal();
    const routeParams = this.getAllRouteParams();

    // ✅ CASO INIZIALE: non loggato / root
    if (!breadcrumbs || breadcrumbs.length === 0) {
      return [{ label: "Home", url: "/" }];
    }

    return breadcrumbs.map((crumb) => {
      let label = crumb.label ?? "";
      let url = crumb.url ?? "";

      // sostituzione label dinamiche {{id}}
      label = label.replace(
        /{{\s*(\w+)\s*}}/g,
        (_, key) => labelData[key] ?? ""
      );

      // sostituzione parametri url :id
      url = url.replace(
        /:([^\/]+)/g,
        (_, key) => routeParams[key] ?? ""
      );

      return { label, url };
    });
  });

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let currentRoute = this.route;
          while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
          }
          return currentRoute;
        }),
        filter((route) => route.outlet === PRIMARY_OUTLET)
      )
      .subscribe((route) => {
        const data = route.snapshot.data["breadcrumb"];
        this.breadcrumbService.updateBreadcrumb(data ?? []);
      });
  }
}
